import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Input, Modal, Select, Spin } from 'antd';
import PropTypes from 'prop-types';
import { fetchMedications } from '../../../medications/api/medicationsApi';
import { upsertTherapyReservation } from '../../api/therapiesApi';
import { getTherapyPatientName } from '../../utils/therapyPresentation';

const initialForm = {
  count: '1',
  medication: null,
  note: '',
};

const countOptions = ['1', '2', '3'];

const TherapyReservationModal = ({
  isOpen,
  onClose,
  therapy,
  reservation,
  onSaved,
  onLoadingChange,
}) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [medicationsLoading, setMedicationsLoading] = useState(false);
  const [medicationSearch, setMedicationSearch] = useState('');

  const isEdit = !!reservation?._id;

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadMedications = async () => {
      setMedicationsLoading(true);

      const { data, ok, message } = await fetchMedications({
        page: 1,
        limit: 10000,
      });

      if (cancelled) return;

      if (!ok) {
        setMedicationOptions([]);
        setError(message || 'Doslo je do greske pri dobavljanju lekova.');
        setMedicationsLoading(false);
        return;
      }

      const options = Array.isArray(data?.medications)
        ? data.medications.map((medication) => ({
            value: medication.name,
            label: medication.name,
          }))
        : [];

      setMedicationOptions(options);
      setMedicationsLoading(false);
    };

    // Kod izmene popuni iz postojece rezervacije; inace predlozi prvi lek terapije.
    const suggestedMedication = Array.isArray(therapy?.medications)
      ? therapy.medications[0]
      : null;

    setForm({
      count: String(reservation?.count || '1'),
      medication: reservation?.medication || suggestedMedication || null,
      note: reservation?.note || '',
    });
    setMedicationSearch('');
    setError('');
    setSuccessMessage('');
    setDone(false);
    setLoading(false);
    setMedicationOptions([]);

    loadMedications();

    return () => {
      cancelled = true;
    };
  }, [isOpen, reservation, therapy]);

  const handleOk = useCallback(async () => {
    if (!therapy?._id) {
      setError('Terapija nije izabrana.');
      return;
    }
    if (!form.medication) {
      setError('Izaberite lek.');
      return;
    }

    const payload = {
      count: Number(form.count),
      medication: form.medication,
    };
    const note = form.note?.trim();
    if (note) {
      payload.note = note;
    }

    setLoading(true);
    onLoadingChange?.(true);
    setError('');
    setSuccessMessage('');

    try {
      const { data, ok, message } = await upsertTherapyReservation(
        therapy._id,
        payload
      );
      if (!ok) {
        setError(message);
        return;
      }

      setSuccessMessage(message || 'Rezervacija je sacuvana.');
      setDone(true);
      await onSaved?.(data);
      setTimeout(() => {
        setDone(false);
        onClose?.();
      }, 800);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [form, onClose, onLoadingChange, onSaved, therapy?._id]);

  const handleCancel = () => {
    setForm(initialForm);
    setMedicationSearch('');
    setError('');
    setSuccessMessage('');
    setDone(false);
    onLoadingChange?.(false);
    onClose?.();
  };

  return (
    <Modal
      className="create_user_modal"
      title="Rezervisi terapiju unapred"
      open={isOpen}
      onCancel={handleCancel}
      footer={[
        <Button
          key="save"
          type="primary"
          onClick={handleOk}
          disabled={loading || done}
          style={{ width: '170px' }}
        >
          {loading ? (
            <img
              src="/assets/icons/add.svg"
              alt="Učitavanje"
              className="animate-spin"
            />
          ) : done ? (
            <img
              src="/assets/icons/check.svg"
              alt="Done"
              className="check-icon"
            />
          ) : isEdit ? (
            'Sacuvaj izmene'
          ) : (
            'Rezervisi'
          )}
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <div className="create_user_modal_form" autoComplete="off">
          <div style={{ marginBottom: 12 }}>
            <strong>Pacijent:</strong> {getTherapyPatientName(therapy)}
          </div>
          <div style={{ marginBottom: 12 }}>
            <label
              htmlFor="reservation-count"
              style={{ display: 'block', marginBottom: 6 }}
            >
              Broj terapija (period)
            </label>
            <select
              id="reservation-count"
              name="count"
              value={form.count}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, count: e.target.value }))
              }
              style={{
                width: '100%',
                height: 38,
                borderRadius: 8,
                border: '1px solid #d9d9d9',
                padding: '0 10px',
              }}
            >
              {countOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
              Relativno od poslednjeg meseca aktuelne terapije.
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label
              htmlFor="reservation-medication-select"
              style={{ display: 'block', marginBottom: 6 }}
            >
              Lek
            </label>
            <Select
              id="reservation-medication-select"
              showSearch
              value={form.medication}
              searchValue={medicationSearch}
              placeholder={
                medicationsLoading
                  ? 'Ucitavanje lekova...'
                  : 'Izaberite lek iz sistema'
              }
              style={{ width: '100%' }}
              loading={medicationsLoading}
              disabled={medicationsLoading}
              options={medicationOptions}
              filterOption={(input, option) =>
                String(option?.label || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              notFoundContent={
                medicationsLoading
                  ? 'Ucitavanje lekova...'
                  : 'Nema dostupnih lekova'
              }
              onSearch={setMedicationSearch}
              onSelect={(value) => {
                setForm((prev) => ({ ...prev, medication: value }));
                setMedicationSearch('');
              }}
            />
          </div>
          <div>
            <label
              htmlFor="reservation-note"
              style={{ display: 'block', marginBottom: 6 }}
            >
              Napomena (opciono)
            </label>
            <Input.TextArea
              id="reservation-note"
              value={form.note}
              rows={3}
              placeholder="Unesite napomenu"
              onChange={(e) =>
                setForm((prev) => ({ ...prev, note: e.target.value }))
              }
            />
          </div>
          {successMessage && (
            <Alert
              title={successMessage}
              type="success"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
          {error && (
            <Alert
              title={error}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      </Spin>
    </Modal>
  );
};

TherapyReservationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  therapy: PropTypes.object,
  reservation: PropTypes.object,
  onSaved: PropTypes.func,
  onLoadingChange: PropTypes.func,
};

export default TherapyReservationModal;
